// services/searchQueue.ts
import { supabase } from './supabase';

interface QueuedSearch {
    query: string;
    customer_id: string | null;
    results_count: number;
    ip_address?: string;
    user_agent?: string;
    timestamp: number;
}

class SearchQueue {
    private queue: QueuedSearch[] = [];
    private isSending = false;
    private readonly STORAGE_KEY = 'ruyo_search_queue';
    private readonly BATCH_SIZE = 10;
    private readonly SEND_INTERVAL = 60 * 60 * 1000;
    private intervalId: NodeJS.Timeout | null = null;
    private uniqueQueriesSet: Set<string> = new Set();

    private async getIpAddress(): Promise<string | null> {
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            return data.ip;
        } catch {
            return null;
        }
    }

    private getUserAgent(): string {
        return navigator.userAgent;
    }

    private async isQueryExistsInDB(query: string): Promise<boolean> {
        try {
            const { data, error } = await supabase
                .from('search_queries')
                .select('query')
                .eq('query', query)
                .limit(1);
            if (error) return false;
            return data && data.length > 0;
        } catch {
            return false;
        }
    }

    async addToQueue(query: string, resultsCount: number = 0) {
        const trimmedQuery = query.trim().toLowerCase();
        if (trimmedQuery.length < 3) return;

        // Проверка дублей
        if (this.queue.some(q => q.query === trimmedQuery)) return;
        if (this.uniqueQueriesSet.has(trimmedQuery)) return;

        const existsInDB = await this.isQueryExistsInDB(trimmedQuery);
        if (existsInDB) {
            this.uniqueQueriesSet.add(trimmedQuery);
            return;
        }

        const userStr = localStorage.getItem('user');
        let customerId = null;
        if (userStr) {
            try { customerId = JSON.parse(userStr).id; } catch {}
        }

        const ipAddress = await this.getIpAddress().catch(() => null);

        this.queue.push({
            query: trimmedQuery,
            customer_id: customerId,
            results_count: resultsCount,
            ip_address: ipAddress || undefined,
            user_agent: this.getUserAgent(),
            timestamp: Date.now()
        });

        this.saveToLocalStorage();
        console.log(`📝 Новый запрос "${trimmedQuery}" (в очереди: ${this.queue.length})`);

        if (this.queue.length >= this.BATCH_SIZE) {
            await this.sendQueue();
        }
    }

    private saveToLocalStorage() {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.queue));
        } catch {}
    }

    private loadFromLocalStorage() {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            if (saved) {
                this.queue = JSON.parse(saved);
                const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
                this.queue = this.queue.filter(q => q.timestamp >= oneDayAgo);
                this.saveToLocalStorage();
            }
        } catch {}
    }

    private async loadExistingQueriesFromDB() {
        try {
            const { data, error } = await supabase
                .from('search_queries')
                .select('query');
            if (error) return;
            data?.forEach(item => this.uniqueQueriesSet.add(item.query));
            console.log(`📚 Загружено ${this.uniqueQueriesSet.size} уникальных запросов из БД`);
        } catch {}
    }

    async sendQueue() {
        if (this.isSending || this.queue.length === 0) return;
        this.isSending = true;
        const batch = [...this.queue];

        try {
            const uniqueBatch = [];
            for (const item of batch) {
                if (!this.uniqueQueriesSet.has(item.query)) {
                    const existsInDB = await this.isQueryExistsInDB(item.query);
                    if (!existsInDB) {
                        uniqueBatch.push(item);
                    } else {
                        this.uniqueQueriesSet.add(item.query);
                    }
                }
            }

            if (uniqueBatch.length === 0) {
                this.queue = [];
                this.saveToLocalStorage();
                return;
            }

            const inserts = uniqueBatch.map(item => ({
                query: item.query,
                customer_id: item.customer_id,
                results_count: item.results_count,
                ip_address: item.ip_address,
                user_agent: item.user_agent,
                created_at: new Date(item.timestamp).toISOString()
            }));

            const { error } = await supabase.from('search_queries').insert(inserts);
            if (error) { console.error('❌ Ошибка:', error); return; }

            uniqueBatch.forEach(item => this.uniqueQueriesSet.add(item.query));
            this.queue = [];
            this.saveToLocalStorage();
            console.log(`✅ Отправлено ${uniqueBatch.length} запросов`);
        } catch (error) {
            console.error('❌ Ошибка:', error);
        } finally {
            this.isSending = false;
        }
    }

    async flush() { await this.sendQueue(); }

    startPeriodicSend() {
        if (this.intervalId) clearInterval(this.intervalId);
        this.intervalId = setInterval(() => this.sendQueue(), this.SEND_INTERVAL);
    }

    stopPeriodicSend() {
        if (this.intervalId) { clearInterval(this.intervalId); this.intervalId = null; }
    }

    getQueue() { return [...this.queue]; }
    getUniqueSet() { return Array.from(this.uniqueQueriesSet); }
    clearQueue() { this.queue = []; this.saveToLocalStorage(); }

    async init() {
        this.loadFromLocalStorage();
        await this.loadExistingQueriesFromDB();
        this.startPeriodicSend();

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') this.flush();
        });
        window.addEventListener('beforeunload', () => {
            if (this.queue.length > 0) this.flush();
        });
        window.addEventListener('online', () => this.sendQueue());
        setTimeout(() => {
            if (this.queue.length > 0) this.sendQueue();
        }, 5000);

        console.log(`✅ SearchQueue инициализирован (в очереди: ${this.queue.length})`);
        if (process.env.NODE_ENV === 'development') {
            (window as any).searchQueue = this;
        }
    }
}

export const searchQueue = new SearchQueue();